// Development-only auth bypass.
//
// When enabled, AuthGate returns a synthetic Session and ProfileSync seeds
// a fake user into localStorage so smoke tests can render Today, Insights,
// KNOW, Care, and Me without a real Supabase login.
//
// Hard guards (ALL must hold for bypass to activate):
//   1. `import.meta.env.DEV` is true OR `import.meta.env.VITE_DEV_AUTH_BYPASS === "1"`.
//      The Vite flag MUST be unset in production builds.
//   2. The page URL has `?devAuth=1`, OR `localStorage.knowmeno_dev_auth === "1"`.
//
// Visiting `/app?devAuth=1` once persists the flag for the tab session.
// Visiting `/app?devAuth=0` clears it.
import type { Session, User } from "@supabase/supabase-js";

export const DEV_AUTH_USER_ID = "00000000-0000-4000-8000-devauthbypass";
const STORAGE_FLAG = "knowmeno_dev_auth";

function envAllows(): boolean {
  if (typeof import.meta === "undefined") return false;
  const env = (import.meta as { env?: Record<string, string | undefined> }).env ?? {};
  if (env.DEV) return true;
  if (env.VITE_DEV_AUTH_BYPASS === "1") return true;
  return false;
}

/** True only when the runtime+flag conditions for bypass are met. */
export function isDevAuthBypassActive(): boolean {
  if (typeof window === "undefined") return false;
  if (!envAllows()) return false;
  try {
    const url = new URL(window.location.href);
    const param = url.searchParams.get("devAuth");
    if (param === "1") {
      window.localStorage.setItem(STORAGE_FLAG, "1");
      return true;
    }
    if (param === "0") {
      window.localStorage.removeItem(STORAGE_FLAG);
      return false;
    }
    return window.localStorage.getItem(STORAGE_FLAG) === "1";
  } catch {
    return false;
  }
}

/** Synthetic Session shaped like Supabase's, safe for client-only code paths. */
export function buildDevAuthSession(): Session {
  const user: User = {
    id: DEV_AUTH_USER_ID,
    aud: "authenticated",
    role: "authenticated",
    email: "dev-bypass@knowmeno.test",
    app_metadata: { provider: "dev-bypass" },
    user_metadata: { dev_bypass: true, name: "Dev Bypass" },
    created_at: new Date(0).toISOString(),
    confirmed_at: new Date(0).toISOString(),
    email_confirmed_at: new Date(0).toISOString(),
    last_sign_in_at: new Date().toISOString(),
    identities: [],
    factors: [],
  } as unknown as User;
  return {
    access_token: "dev-bypass-access-token",
    refresh_token: "dev-bypass-refresh-token",
    token_type: "bearer",
    expires_in: 3600,
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    user,
  } as Session;
}

/** True when a Session was minted by buildDevAuthSession. */
export function isDevAuthSession(session: { user?: { id?: string | null } } | null | undefined): boolean {
  return session?.user?.id === DEV_AUTH_USER_ID;
}
